/* eslint-disable max-lines */
/* eslint-disable dot-notation */

import { action, intercept, observable, runInAction } from 'mobx';
import { AnalyticsStore, ContextStore, ExperimentStore, Store } from 'bernie-plugin-mobx';

import { getBexApiContext } from 'bernie-context';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';
import { SerializedData } from 'bernie-core';

import { BexApiSource } from 'sources/bex-api-source';

import { getDefaultTimeout } from 'stores/multi-item-store-util';
import { getPropertiesFromResults, getPropertySearchInput } from 'stores/adapters';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import { DefaultRequestFields, HotelMapModel, PropertyLocationScoreInfoModel, PropertyRoomScoreInfoModel, RateCalendarModel } from 'models';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import {
  ContextInput,
  LodgingCardFields,
  LodgingPwaPropertySearch,
  PropertyFields,
  PropertyLocationScoreInfo,
  PropertyRoomScoreInfo,
  PropertySearchTravelAds,
} from '__generated__/api';
import { mapLodgingCard } from './adapters/bex-api/search/lodging-card-adapter';
import isPayload, { Payload } from './adobe-payload';

interface CurrentHotelStoreDefaults {
  hotelId?: number;
  selectedRoomType: string;
  selectedRatePlan: string;
  multiItemPriceToken?: string | undefined;
  srpDisplayPrice?: number | undefined;
  srpCurrency?: string | undefined;
  l10n: ILocalization;
  offerSearchData: Record<string, unknown>;
  reviewIdsUpvoted: string[];
  poiData: Record<string, unknown>;
  propertyRoomScoreInfo: PropertyRoomScoreInfoModel[];
  propertyLocationScoreInfo: PropertyLocationScoreInfoModel;
  selectedGalleryCategoryId: string;
  referrer?: string | undefined;
  offerTimestamp?: string | undefined;
}

export class CurrentHotelStore extends Store {
  private SIMILAR_HOTELS_TO_SHOW: number = 10;

  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, l10n: ILocalization, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.l10n = l10n;

    this.searchCriteria = new SearchCriteriaModel({}, this.l10n);
    this.numberInterceptor('hotelId');
  }

  public hydrate(data: SerializedData): void {
    if (data && data.hotel && data.hotel.code === 'INACTIVE_HOTEL_ID') {
      window.location.replace(`https://${window.location.host}/Hotels`);
    }

    delete data.bexApiSource;
    delete data.hotelSearchApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;

    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public numberInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeNumber(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
      });
    }
  }

  public sanitizeNumber(number): number | string {
    try {
      const cleanNumber = typeof number === 'number' ? number : parseInt(number, 10);

      if (Number.isNaN(cleanNumber)) {
        throw new Error(`Invalid Number: cannot parse ${number} into a Number`);
      }

      return cleanNumber;
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.SANATIZE_NUMNBER_ERROR, getErrorString(error));

      return '';
    }
  }

  public static DEFAULTS: CurrentHotelStoreDefaults = {
    hotelId: null,
    selectedRoomType: '',
    selectedRatePlan: '',
    multiItemPriceToken: '',
    srpDisplayPrice: null,
    srpCurrency: '',
    l10n: new Localization('en_US'),
    offerSearchData: {},
    reviewIdsUpvoted: [],
    propertyRoomScoreInfo: [],
    propertyLocationScoreInfo: {},
    poiData: {},
    selectedGalleryCategoryId: 'all',
  };

  @observable
  public l10n: ILocalization;
  @observable
  public toolbar: any;
  @observable
  public property: Partial<PropertyFields.Fragment>;
  @observable
  public map: HotelMapModel;
  @observable
  public propertyRoomScoreInfo: PropertyRoomScoreInfoModel[] = CurrentHotelStore.DEFAULTS.propertyRoomScoreInfo;
  @observable
  public propertyLocationScoreInfo: PropertyLocationScoreInfoModel = CurrentHotelStore.DEFAULTS.propertyLocationScoreInfo;

  @observable
  public hotelId: number;
  @observable
  public selectedRoomType: string;
  @observable
  public selectedRatePlan: string;
  @observable
  public multiItemPriceToken: string;
  @observable
  public srpDisplayPrice: number;
  @observable
  public srpCurrency: string;
  @observable
  public referrer: string;
  @observable
  public offerTimestamp: string;
  @observable
  public isLoading: boolean;
  @observable
  public isFailure: boolean;
  @observable
  public shouldFetch: boolean;
  @observable
  public openNewDatePicker: boolean = false;
  @observable
  public loadingETPData: boolean;
  @observable
  public creatingConversation: boolean;
  @observable
  public similarHotels: PropertySearchTravelAds.Properties[];

  @observable
  public offerSearchData: any;

  @observable
  public travelAd: any;
  @observable
  public requests: any;

  @observable
  public rateCalendar: RateCalendarModel;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  @observable
  public poiData: Record<string, unknown> = CurrentHotelStore.DEFAULTS.poiData;

  private _fetchingHotel: number;

  public selectedMapMarker: number;

  public offerExtensions: any;

  public defaultRequestFields: DefaultRequestFields;

  public apolloLinkSetOnBrowser: boolean = false;
  public apolloLinkSetOnServer: boolean = false;

  private detailsPayload: Payload;
  private offersPayload: Payload;

  public showPetsOnSingleOffer: boolean = false;

  public hotel: any;

  private resetPayloads() {
    this.detailsPayload = null;
    this.offersPayload = null;
  }

  public addPayload(operation: string, payload: Record<string, unknown>, analytics: AnalyticsStore): void {
    if (isPayload(payload)) {
      if (operation === 'PropertyDetailsQuery') {
        this.detailsPayload = payload;
      }
      if (operation === 'PropertyOffersQuery') {
        this.offersPayload = payload;
      }

      if (this.offersPayload && this.detailsPayload) {
        analytics.publishClientSidePayload([this.detailsPayload, this.formatOmnitureData(this.offersPayload)]);
        this.resetPayloads();
      }
    }
  }

  // Temporary fields to hold the raw bex-api responses to be passed to the Offers shared-ui
  // component. Will be cleaned up once the shared-ui components query the fields themselves.
  @observable
  public sharedUiPropertyReviewAggregatesResponse: PropertyRoomScoreInfo.PropertyReviewAggregates | undefined;

  @action
  public setPropertyListingAsCurrentHotel(property?: PropertyFields.Fragment, lodgingCard?: LodgingCardFields.Fragment): void {
    if (!property && !lodgingCard) {
      return;
    }

    const adaptedProperty = lodgingCard ? mapLodgingCard(lodgingCard) : property;

    const { id, name, imageGallery, shortList, star, starRatingIcon, vipMessaging } = adaptedProperty as PropertyFields.Fragment;

    const propertyId = parseInt(id, 10);

    this.toolbar = {
      name,
      stars: star,
    };

    if (this.hotelId === propertyId) {
      return;
    }

    if (
      this.selectedRoomType !== CurrentHotelStore.DEFAULTS.selectedRoomType &&
      this.selectedRatePlan !== CurrentHotelStore.DEFAULTS.selectedRatePlan
    ) {
      this.selectedRoomType = CurrentHotelStore.DEFAULTS.selectedRoomType;
      this.selectedRatePlan = CurrentHotelStore.DEFAULTS.selectedRatePlan;
    }

    this.shouldFetch = true;

    this.property = {
      id,
      name,
      imageGallery,
      shortList,
      star,
      starRatingIcon,
      vipMessaging,
    };

    this.hotelId = propertyId;
  }

  @action
  public updateSearchCriteria(criteria: SearchCriteriaModel): void {
    this.searchCriteria = criteria;
  }

  @action
  public setHotelId(hotelId) {
    const hobPropertyId = this.searchCriteria.expediaPropertyId;
    if (hotelId !== this.hotelId) {
      this.shouldFetch = true;
      this.hotelId = hobPropertyId || hotelId;
    }
  }

  public getRoomScore(roomTypeCode: string): PropertyRoomScoreInfoModel | undefined {
    return this.propertyRoomScoreInfo?.find(x => x.id === roomTypeCode);
  }

  private formatOmnitureData = (omnitureData: Payload) => {
    const firstAttribute = omnitureData.adobeMappedData?.products?.split('=')?.find(Boolean);
    const hotelString = `;Hotel:${this.hotelId};;;`;
    omnitureData.adobeMappedData.products = `${hotelString}${omnitureData.adobeMappedData.products}`;
    if (firstAttribute) {
      omnitureData.adobeMappedData.products = omnitureData.adobeMappedData.products.replace(
        new RegExp(`&${firstAttribute}`, 'g'),
        `,${hotelString}${firstAttribute}`
      );
    }

    return omnitureData;
  };

  private logErrorEvent(name, err) {
    this.logger.logEvent(name, getErrorString(err));
  }

  @action
  public setOfferData(offerData): void {
    this.offerSearchData = Object.assign({}, this.offerSearchData, offerData);
  }

  @action
  public fetchPropertyLocationScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyLocationScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };

    return this.bexApiSource
      .fetchPropertyLocationScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        runInAction('update propertyLocationScoreInfo from response', () => {
          if (data?.propertyReviewAggregates?.propertySummaries?.length) {
            this.propertyLocationScoreInfo = data.propertyReviewAggregates.propertySummaries[0].locationScore;
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api property info location score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public fetchPropertyRoomScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyRoomScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };
    this.propertyRoomScoreInfo = null;

    return this.bexApiSource
      .fetchPropertyRoomScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        if (data?.propertyReviewAggregates?.propertySummaries?.length) {
          this.propertyRoomScoreInfo = data.propertyReviewAggregates.propertySummaries[0].roomsScore;
          this.sharedUiPropertyReviewAggregatesResponse = data.propertyReviewAggregates;
        } else {
          this.sharedUiPropertyReviewAggregatesResponse = {
            __typename: 'PropertyReviewAggregates',
          };
        }
      })
      .catch(error => {
        runInAction('recovering from bex api property info room score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  /**
   * Fetches sponsored hotels (MeSo) shown on the dateless pdp page.
   * These may or may not be very different from the actual current hotel.
   * @param context
   * @param experiments
   * @param multiItemContext
   */
  @action
  public fetchSimilarHotels(context: ContextStore, multiItemContext: MultiItemContext, pageId: string, experiments?: ExperimentStore) {
    this.similarHotels = null;

    if (!this.hotelId || !this.searchCriteria || !this.searchCriteria.regionId || this.searchCriteria.packageRates === 'true') {
      return Promise.resolve();
    }

    const criteriaWithParsedGuests = SearchQueryAdapter.parseOut(this.searchCriteria && this.searchCriteria.toJSON());

    const similarHotelsCriteria = {
      ...criteriaWithParsedGuests,
      count: this.SIMILAR_HOTELS_TO_SHOW,
      rooms: this.searchCriteria.rooms,
    };

    if (!this.searchCriteria.star) {
      similarHotelsCriteria.star = '10,20,30,40,50';
    }

    const promise: Promise<any> = this.fetchTravelAdPropertySearch(context, similarHotelsCriteria, multiItemContext, pageId, experiments);

    return promise
      .then(response => {
        runInAction('updating dated HIS travelAd carousel results', () => {
          if (response.data) {
            this.similarHotels = getPropertiesFromResults(response.data);
          } else {
            this.similarHotels = response;
          }
        });
      })
      .catch(error => {
        runInAction('dated HIS carousel similar hotels call failure', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  private fetchTravelAdPropertySearch(
    context: ContextStore,
    criteria: SearchCriteriaModel,
    multiItemContext: MultiItemContext,
    pageId: string,
    experiments?: ExperimentStore
  ) {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(
      context,
      criteria,
      multiItemContext,
      true,
      null,
      experiments
    );
    if (variables.criteria) {
      variables.criteria.primary.destination.pinnedPropertyId = this.hotelId.toString();
    }

    return this.bexApiSource.fetchTravelAdPropertySearch(variables, pageId);
  }

  @action
  public clearPoiData() {
    this.poiData = CurrentHotelStore.DEFAULTS.poiData;
  }

  public setSelectedMapMarker(markerId: number) {
    this.selectedMapMarker = markerId ? Number(markerId) : markerId;
  }
}
